import { useState } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";

import { BaseAppGlobalPopUp } from "./BaseAppGlobalPopUp";
import { Selector } from "../../Common/_components/Selector";
import { PopUpStore } from "../../Common/_stores/PopUpStore";
import { PMSLocationsActions } from "../../PMS/_actions/PMSLocationsActions";
import {
    integrationTemplateLabel,
    isDifferentAccount,
} from "../../PMS/_helpers/IntegrationTemplateHelper";
import { Integration } from "../_modelsTS/Integration";

type MoveIntegrationTemplateGlobalPopUpProps = {
    integrationUid: string;
    // Templates of the same company the caller offers; the current one is
    // filtered out here so the picker never shows a no-op.
    templates: Integration[];
    currentTemplateUid?: string | null;
    // What the API says the current account is called. The template itself may
    // not be listable by this user -- a colleague's personal account never is --
    // and without it the dialog would claim there is none while the row behind
    // it shows the name.
    currentTemplateName?: string | null;
    // The rooms the integration already has: the backend takes `rooms` as the
    // full assignment on every update, so they travel with the move.
    rooms: string[];
};

export function MoveIntegrationTemplateGlobalPopUp({
    integrationUid,
    templates,
    currentTemplateUid,
    currentTemplateName,
    rooms,
}: MoveIntegrationTemplateGlobalPopUpProps) {
    //LIBRARIES
    const { t } = useTranslation();
    const dispatch = useDispatch();

    //LOCAL STATE
    const [selectedUid, setSelectedUid] = useState<string | null>(null);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [isSaving, setIsSaving] = useState(false);

    //DERIVED
    const currentTemplate = templates.find(({ uid }) => uid === currentTemplateUid);
    const options = templates
        .filter(({ uid }) => uid !== currentTemplateUid)
        .map(template => ({
            value: template.uid,
            label: integrationTemplateLabel(template),
        }));
    const selectedTemplate = templates.find(({ uid }) => uid === selectedUid);
    const warnsAboutAccount = isDifferentAccount(currentTemplate, selectedTemplate);

    //VIEW
    return (
        <BaseAppGlobalPopUp
            title={t("mod_pms_integration_move_template_title")}
            body={body()}
            // Kept open on accept so a rejected move (another PMS company, a
            // webhook-bound one, a template of another organization) can be
            // explained in place instead of vanishing behind a toast.
            closeOnAccept={false}
            disableAccept={!selectedUid || isSaving}
            overflowVisible
            onAccept={handleMove}
        />
    );

    //ACTION HANDLERS
    async function handleMove() {
        setErrorMessage(null);
        setIsSaving(true);
        try {
            await dispatch(PMSLocationsActions.moveLocationIntegrationTemplate(
                integrationUid,
                selectedUid!,
                rooms,
            ));
            // Hidden last: the component is unmounted with it, so anything
            // after this would be a state update on a dead component.
            setIsSaving(false);
            dispatch(PopUpStore.actionHide());
        } catch (error: any) {
            setErrorMessage(error.message);
            setIsSaving(false);
        }
    }

    function body() {
        return (
            <div className="text-left">
                <div className="mb-3">
                    {t("mod_pms_integration_move_template_description")}
                </div>
                <div className="mb-2">
                    <span className="text-secondary">
                        {t("mod_pms_integration_template")}:{" "}
                    </span>
                    {currentTemplateUid
                        ? (integrationTemplateLabel(currentTemplate)
                           || currentTemplateName
                           || t("mod_pms_integration_no_template"))
                        : t("mod_pms_integration_no_template")}
                </div>
                <Selector
                    autoFocus
                    placeholder={t("mod_pms_integration_move_template_placeholder")}
                    value={options.find(({ value }) => value === selectedUid) ?? null}
                    options={options}
                    onChange={(option: any) => setSelectedUid(option?.value ?? null)}
                />
                {warnsAboutAccount && (
                    <div className="small text-warning mt-3">
                        {t("mod_pms_integration_move_template_other_account_warning")}
                    </div>
                )}
                {errorMessage && (
                    <div className="small text-danger mt-3">{errorMessage}</div>
                )}
            </div>
        );
    }
}
